import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { translations as bundled } from '../data/mock';
import { fetchPageTexts } from '../services/api';

const LanguageContext = createContext();

// Recursively merge B into A (B wins on leaf collisions; non-empty strings only)
const deepMerge = (a, b) => {
  if (b === null || b === undefined) return a;
  if (typeof a !== 'object' || a === null || Array.isArray(a)) return b;
  const out = { ...a };
  for (const k of Object.keys(b)) {
    if (b[k] === undefined || b[k] === null) continue;
    // Empty string overrides are ignored — fall back to bundled default so
    // admin clearing a field cannot wipe a label from the public site.
    if (typeof b[k] === 'string' && b[k].trim() === '') continue;
    if (typeof b[k] === 'object' && !Array.isArray(b[k])) {
      out[k] = deepMerge(a?.[k] ?? {}, b[k]);
    } else {
      out[k] = b[k];
    }
  }
  return out;
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState('tr');
  const [overrides, setOverrides] = useState({ tr: {}, en: {} });

  useEffect(() => {
    fetchPageTexts().then((data) => {
      if (data && (data.tr || data.en)) setOverrides({ tr: data.tr || {}, en: data.en || {} });
    });
  }, []);

  const toggleLanguage = useCallback(() => {
    setLanguage((prev) => (prev === 'tr' ? 'en' : 'tr'));
  }, []);

  const t = useCallback(
    (key) => {
      const merged = deepMerge(bundled[language] || {}, overrides[language] || {});
      const keys = key.split('.');
      let value = merged;
      for (const k of keys) {
        value = value?.[k];
      }
      return value === undefined || value === null ? key : value;
    },
    [language, overrides]
  );

  return (
    <LanguageContext.Provider value={{ language, toggleLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};
